import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { combineLatest, Observable, of, Subject } from "rxjs";
import {
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
} from "rxjs/operators";
import { MenuService } from "@/_services/menu.service";
import { USER_IS_AT_LEAST_ADMIN } from "@/_services";
import { ContextualNavService } from "@/_services/contextual-nav.service";
import { NewReportSidebarService } from "@/_services/new-report-sidebar.service";
import { AssessmentDto } from "@/_models";
import { MeetingMinutesFeature } from "@features/MeetingMinutesFeature";
import {
    MobileOptionsMenuService,
    OptionsMenuItem,
} from "@/_services/mobile-options-menu.service";

export interface ContextualSibling {
    label: string;
    link: string;
    query?: Record<string, string>;
}

export interface ContextualNavConfig {
    parentLabel: string;
    currentLabel: string;
    siblings: ContextualSibling[];
    showAdd: boolean;
    /** From `MenuService.formMenuAssessments$` (same stream as `createFormMenu`). */
    addAssessment?: AssessmentDto;
    addRoute?: string;
    addQueryParams?: Record<string, string>;
    useAddAction?: boolean;
    addIcon?: string;
}

@Component({
    selector: "app-mobile-contextual-bar",
    templateUrl: "./mobile-contextual-bar.component.html",
    styleUrls: ["./mobile-contextual-bar.component.scss"],
})
export class MobileContextualBarComponent implements OnInit, OnDestroy {
    config$: Observable<ContextualNavConfig | null>;

    private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private menuService: MenuService,
        private contextualNavService: ContextualNavService,
        private newReportSidebarService: NewReportSidebarService,
        private mobileOptionsMenu: MobileOptionsMenuService,
        @Inject(USER_IS_AT_LEAST_ADMIN)
        private isAtLeastAdmin$: Observable<boolean>,
    ) {}

    ngOnInit(): void {
        this.config$ = this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            startWith(null),
            map(() => this.router.url),
            switchMap((url) => this.resolveConfig(url)),
            shareReplay(1),
            takeUntil(this.destroy$),
        );
    }

    private resolveConfig(url: string): Observable<ContextualNavConfig | null> {
        const path = url.split("?")[0];
        const query = new URLSearchParams(url.split("?")[1] || "");

        if (path.startsWith("/performance")) {
            return of(this.getPerformanceConfig(path));
        } else if (path.startsWith("/assurance")) {
            return of(this.getAssuranceConfig(path));
        } else if (path.startsWith("/policies")) {
            return of(this.getPolicyConfig(path));
        } else if (path.startsWith("/manuals")) {
            return of(this.getManualsConfig(query.get("type")));
        } else if (path.startsWith("/profile")) {
            return this.getProfileConfig$(path);
        } else if (path.startsWith("/promotions")) {
            return this.getPromotionConfig$(path);
        } else if (path.startsWith("/risk-management")) {
            return of(this.getRiskManagementConfig(path));
        } else if (path.startsWith("/assessments")) {
            return this.getAssessmentsConfig$(path);
        }

        return of(null);
    }

    private getPerformanceConfig(path: string): ContextualNavConfig {
        const siblings: ContextualSibling[] = [
            { label: "Reports", link: "/performance/reports" },
            { label: "Assurance", link: "/performance/assurance" },
        ];
        const currentLabel = path.includes("/performance/assurance")
            ? "Assurance"
            : "Reports";
        return {
            parentLabel: "Performance",
            currentLabel,
            siblings,
            showAdd: false,
        };
    }

    private getAssuranceConfig(path: string): ContextualNavConfig {
        const siblings: ContextualSibling[] = [
            { label: "Internal Audit", link: "/assurance/internal-audit" },
            { label: "Case Registry", link: "/assurance/registry" },
            { label: "Action Register", link: "/assurance/register-actions" },
            { label: "Safety Advisories", link: "/assurance/advisories" },
        ];

        let currentLabel = "Internal Audit";
        let showAdd = false;
        let addRoute: string | undefined;

        if (path.includes("/registry")) {
            currentLabel = "Case Registry";
            showAdd = true;
        } else if (path.includes("/register-actions")) {
            currentLabel = "Action Register";
            showAdd = true;
            // Mobile "+" opens the same create flow as the desktop "New Task"
            // CTA by navigating straight to the create route.
            addRoute = "/assurance/register-actions/new";
        } else if (path.includes("/advisories")) {
            currentLabel = "Safety Advisories";
        } else if (path === "/assurance/internal-audit") {
            showAdd = true;
        }

        return {
            parentLabel: "Safety Assurance",
            currentLabel,
            siblings,
            showAdd,
            addRoute,
            useAddAction: showAdd && !addRoute,
        };
    }

    private getPolicyConfig(path: string): ContextualNavConfig {
        const siblings: ContextualSibling[] = [
            {
                label: "Safety Policy Statement",
                link: "/policies/statement",
            },
            { label: "Safety Objectives", link: "/policies/objectives" },
        ];
        const isObjectives = path.includes("/objectives");
        const currentLabel = isObjectives
            ? "Safety Objectives"
            : "Safety Policy Statement";
        return {
            parentLabel: "Safety Policy",
            currentLabel,
            siblings,
            showAdd: isObjectives,
            useAddAction: isObjectives,
        };
    }

    private getManualsConfig(type: string | null): ContextualNavConfig {
        const siblings: ContextualSibling[] = [
            {
                label: "Manuals",
                link: "/manuals",
                query: { type: "manual" },
            },
            {
                label: "General",
                link: "/manuals",
                query: { type: "general" },
            },
        ];
        const currentLabel = type === "general" ? "General" : "Manuals";
        return {
            parentLabel: "Documents",
            currentLabel,
            siblings,
            showAdd: true,
            useAddAction: true,
        };
    }

    private getProfileConfig$(path: string): Observable<ContextualNavConfig> {
        return this.menuService.createProfileMenu().pipe(
            map((tabs) => {
                const siblings: ContextualSibling[] = tabs.map((tab) => ({
                    label: tab.label,
                    link: `/profile/${tab.link}`,
                    query: tab.query,
                }));

                const currentTab =
                    tabs.find((tab) => {
                        const prefix = `/profile/${tab.link}`;
                        return path === prefix || path.startsWith(`${prefix}/`);
                    }) ?? tabs[0];
                const isMembersTab = currentTab?.link === "users";
                const isAircraftTab = currentTab?.link === "aircraft";
                const isRolesTab = currentTab?.link === "roles";
                const isGroupsTab = currentTab?.link === "groups";

                return {
                    parentLabel: "Account Settings",
                    currentLabel: currentTab?.label ?? "Profile",
                    siblings,
                    showAdd:
                        isMembersTab ||
                        isAircraftTab ||
                        isRolesTab ||
                        isGroupsTab,
                    addRoute: isMembersTab
                        ? "/profile/invite"
                        : isAircraftTab
                          ? "/profile/aircraft/add"
                          : undefined,
                    useAddAction: isRolesTab || isGroupsTab,
                    addIcon: isRolesTab ? "icon-download" : undefined,
                };
            }),
        );
    }

    private getPromotionConfig$(path: string): Observable<ContextualNavConfig> {
        const siblings: ContextualSibling[] = [
            { label: "Promotions", link: "/promotions/promotions" },
        ];
        if (MeetingMinutesFeature.isEnabled()) {
            siblings.push({
                label: "Meeting Minutes",
                link: "/promotions/meeting-minutes",
            });
        }

        const currentLabel = path.includes("/meeting-minutes")
            ? "Meeting Minutes"
            : "Promotions";

        // Add (New Promotion / New Meeting) is admin-only, matching the desktop
        // list pages. The active list component handles onAddAction$.
        return this.isAtLeastAdmin$.pipe(
            map((isAdmin) => ({
                parentLabel: "Safety Promotion",
                currentLabel,
                siblings,
                showAdd: isAdmin,
                useAddAction: isAdmin,
            })),
        );
    }

    private getRiskManagementConfig(path: string): ContextualNavConfig {
        const siblings: ContextualSibling[] = [
            { label: "Risk Evaluations", link: "/risk-management/evaluations" },
            {
                label: "Hazard Register",
                link: "/risk-management/hazard-register",
            },
            {
                label: "Control Library",
                link: "/risk-management/control-library",
            },
        ];

        let currentLabel = "Risk Evaluations";

        if (path.includes("/hazard-register")) {
            currentLabel = "Hazard Register";
        } else if (path.includes("/control-library")) {
            currentLabel = "Control Library";
        }

        return {
            parentLabel: "Safety Risk Management",
            currentLabel,
            siblings,
            showAdd: true,
            useAddAction: true,
        };
    }

    private getAssessmentsConfig$(
        path: string,
    ): Observable<ContextualNavConfig | null> {
        return combineLatest([
            this.menuService.createFormMenu(),
            this.menuService.formMenuAssessments$,
        ]).pipe(
            map(([formMenu, assessments]) => {
                const siblings: ContextualSibling[] = formMenu.items.map(
                    (item) => ({
                        label: item.label,
                        link: Array.isArray(item.routerLink)
                            ? item.routerLink.join("/")
                            : (item.routerLink as string),
                    }),
                );

                const pathParts = path.split("/").filter(Boolean);
                const formId = pathParts.length > 1 ? pathParts[1] : null;

                const isNew = formId === "new";
                // List page: /assessments/[ID] (exactly 2 path segments)
                const isListPage = !!formId && !isNew && pathParts.length === 2;

                let currentLabel = isNew ? "New Form" : "Forms";
                if (formId && !isNew) {
                    const matched = formMenu.items.find((item) => {
                        const link = Array.isArray(item.routerLink)
                            ? item.routerLink.join("/")
                            : (item.routerLink as string);
                        return link?.includes(formId);
                    });
                    if (matched) currentLabel = matched.label;
                }

                const addAssessment =
                    isListPage && formId
                        ? assessments.find((a) => a._id === formId)
                        : undefined;

                return {
                    parentLabel: "Forms",
                    currentLabel,
                    siblings,
                    showAdd: isNew || (isListPage && !!addAssessment),
                    useAddAction: isNew,
                    addAssessment,
                };
            }),
        );
    }

    goBack(): void {
        this.router.navigate(["/home"]);
    }

    openSwitcher(config: ContextualNavConfig): void {
        const items: OptionsMenuItem[] = config.siblings.map((sibling) => ({
            label: sibling.label,
            active: sibling.label === config.currentLabel,
            link: sibling.link,
            queryParams: sibling.query,
        }));
        this.mobileOptionsMenu.open(items, config.parentLabel);
    }

    triggerAdd(config: ContextualNavConfig): void {
        if (config.addAssessment) {
            this.openNewReportWithAssessment(config.addAssessment);
            return;
        }
        if (config.addRoute) {
            const qp = config.addQueryParams;
            if (qp && Object.keys(qp).length > 0) {
                this.router.navigate([config.addRoute], {
                    queryParams: qp,
                });
            } else {
                this.router.navigateByUrl(config.addRoute);
            }
        } else if (config.useAddAction) {
            this.contextualNavService.triggerAdd();
        }
    }

    // remove navigate url after ReportsV2Featureir deleted
    private openNewReportWithAssessment(assessment: AssessmentDto): void {
        this.newReportSidebarService.requestOpen(assessment);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
